import Head from 'next/head';
import Link from 'next/link';
import {
  MousePointerClick,
  Package,
  Banknote,
  Search as SearchIcon, // avoid conflict with Search component
  ExternalLink,
} from 'lucide-react';
import { createGetServerSideProps } from '@/lib/serverSideProps/createDefaultServerSideProps';
import getServerSideTranslations from '@/utilites/translations/get-server-side-translations.translations.utils';
import SearchProvider from '@/components/SearchProvider';
import SellSearch from '@/components/sell/SellSearch';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import SellEnum from '@/types/translations/SellEnum';

export default function SellByYourself(): JSX.Element {
  const { translate } = useTypedTranslation<SellEnum>('sell');

  const steps = [
    {
      icon: SearchIcon,
      title: translate(SellEnum['by_yourself_page.steps.1.title']),
      description: translate(SellEnum['by_yourself_page.steps.1.description']),
    },
    {
      icon: MousePointerClick,
      title: translate(SellEnum['by_yourself_page.steps.2.title']),
      description: translate(SellEnum['by_yourself_page.steps.2.description']),
    },
    {
      icon: Package,
      title: translate(SellEnum['by_yourself_page.steps.3.title']),
      description: translate(SellEnum['by_yourself_page.steps.3.description']),
      links: [
        { text: translate(SellEnum['by_yourself_page.steps.3.link_auth']), href: '/help' },
        { text: translate(SellEnum['by_yourself_page.steps.3.link_shipping']), href: '/help' },
      ],
    },
    {
      icon: Banknote,
      title: translate(SellEnum['by_yourself_page.steps.4.title']),
      description: translate(SellEnum['by_yourself_page.steps.4.description']),
    },
  ];

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-text tw-antialiased">
      <Head>
        <title>{translate(SellEnum['by_yourself_page.metadata.title'])}</title>
        <meta
          name="description"
          content={translate(SellEnum['by_yourself_page.metadata.description'])}
        />
      </Head>

      {/* Title Section */}
      <div className="tw-w-full tw-bg-primary-gradient tw-py-16 md:tw-py-24">
        <div className="tw-mx-auto tw-max-w-[1000px] tw-px-4 tw-text-center">
          <div className="tw-mx-auto tw-mb-8 tw-h-[2px] tw-w-16 tw-bg-[#C2AD7D]"></div>
          <h1 className="tw-mb-4 tw-font-prata tw-text-4xl tw-leading-tight tw-text-white sm:tw-text-5xl lg:tw-text-[56px]">
            {translate(SellEnum['by_yourself_page.hero.title'])}
          </h1>
          <p className="tw-text-lg tw-text-white/80 lg:tw-text-xl">
            {translate(SellEnum['by_yourself_page.hero.subtitle'])}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="tw-mx-auto tw-max-w-[1000px] tw-px-4 tw-py-16 md:tw-py-24">
        {/* Search Section */}
        <div className="tw-mx-auto tw-max-w-2xl tw-text-center">
          <h2 className="tw-mb-3 tw-font-prata tw-text-3xl tw-text-text sm:tw-text-4xl">
            {translate(SellEnum['by_yourself_page.search.title'])}
          </h2>
          <p className="tw-mb-8 tw-text-lg tw-text-gray-600">
            {translate(SellEnum['by_yourself_page.search.description'])}
          </p>

          <SearchProvider>
            <SellSearch placeholder={translate(SellEnum['by_yourself_page.search.placeholder'])} />
          </SearchProvider>

          <p className="tw-text-sm tw-font-medium tw-text-gray-500">
            {translate(SellEnum['by_yourself_page.search.fees_info'])}
          </p>
        </div>

        {/* Steps Grid */}
        <div className="tw-mt-24 tw-grid tw-gap-8 md:tw-grid-cols-2">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className="tw-flex tw-flex-col tw-gap-6 tw-rounded-3xl tw-border tw-border-black/10 tw-bg-white tw-p-8 tw-shadow-2xl"
              >
                <div className="tw-relative tw-self-start">
                  <div className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#dbeafe] tw-text-main">
                    <Icon className="tw-h-8 tw-w-8" strokeWidth={2} />
                  </div>
                  <div className="tw-absolute tw--right-2 tw--top-2 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-main tw-text-base tw-font-bold tw-text-white tw-ring-4 tw-ring-white">
                    {index + 1}
                  </div>
                </div>

                <div className="tw-flex tw-flex-grow tw-flex-col">
                  <h3 className="tw-mb-3 tw-text-xl tw-font-bold tw-leading-tight tw-text-text">
                    {step.title}
                  </h3>
                  <p className="tw-flex-grow tw-text-base tw-text-gray-500">{step.description}</p>

                  {step.links && (
                    <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-2">
                      {step.links.map((link, i) => (
                        <Link
                          key={i}
                          href={link.href}
                          className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-main hover:tw-underline"
                        >
                          <ExternalLink className="tw-h-4 tw-w-4" />
                          {link.text}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export const getServerSideProps = createGetServerSideProps(async context => {
  const [translationsProps] = await Promise.all([
    getServerSideTranslations(context, ['common', 'sell', 'header']),
  ]);

  return {
    props: {
      ...translationsProps,
    },
  };
});
